import React, { useState, useRef, useEffect } from 'react';
import './MatchingHalvesExercise.css';

const MHIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h4" />
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
    <path d="M9 12h6" />
  </svg>
);

const EditIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const MatchingHalvesExercise = ({ sentences, title, level, onClose, updateAttributes, isStudent = false, myState = {}, otherState = {}, updateState = () => {} }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editableSentences, setEditableSentences] = useState([...sentences]);
  const [editableTitle, setEditableTitle] = useState(title);

  // Game state
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [matchedPairs, setMatchedPairs] = useState([]); // { leftIndex, rightValue }
  const [errorPair, setErrorPair] = useState(null);     // { leftIndex, rightValue }
  const [flashPair, setFlashPair] = useState(null);     // { leftIndex, rightValue }
  const [shuffledRight, setShuffledRight] = useState([]);
  const [resultsVisible, setResultsVisible] = useState(false);
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [firstTrySuccess, setFirstTrySuccess] = useState(0);
  const [attemptsPerSentence, setAttemptsPerSentence] = useState({});

  const containerRef = useRef(null);
  const addBtnRef = useRef(null);
  const syncedRef = useRef(false);
  const prevResetAtRef = useRef(null);
  const shuffleInitializedRef = useRef(false);

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // --- Shuffle init: broadcast own order so both sides can sync ---
  useEffect(() => {
    if (editableSentences.length === 0 || shuffleInitializedRef.current) return;
    shuffleInitializedRef.current = true;
    const rightHalves = editableSentences.map(s => s.right);
    const shuffled = shuffleArray([...rightHalves]);
    setShuffledRight(shuffled);
    updateState({ shuffledRight: shuffled });
  }, [editableSentences]); // eslint-disable-line

  // --- Restore own persisted state on mount ---
  useEffect(() => {
    if (syncedRef.current) return;
    const hasProgress = myState.matchedPairs && myState.matchedPairs.length > 0;
    if (!hasProgress) return;
    syncedRef.current = true;
    setMatchedPairs(myState.matchedPairs);
    if (myState.resultsVisible || myState.matchedPairs.length === sentences.length) setResultsVisible(true);
    if (typeof myState.wrongAttempts === 'number') setWrongAttempts(myState.wrongAttempts);
    if (typeof myState.firstTrySuccess === 'number') setFirstTrySuccess(myState.firstTrySuccess);
  }, [myState.matchedPairs]); // eslint-disable-line

  // --- Real-time: adopt other side's shuffled order ---
  useEffect(() => {
    if (!Array.isArray(otherState.shuffledRight) || otherState.shuffledRight.length === 0) return;
    setShuffledRight(otherState.shuffledRight);
  }, [otherState.shuffledRight]); // eslint-disable-line

  // --- Real-time: mirror other side's matched pairs ---
  useEffect(() => {
    if (!Array.isArray(otherState.matchedPairs)) return;
    setMatchedPairs(otherState.matchedPairs);
  }, [otherState.matchedPairs]); // eslint-disable-line

  // --- Real-time: show results when other side finishes ---
  useEffect(() => {
    if (!otherState.resultsVisible) return;
    setResultsVisible(true);
    if (typeof otherState.wrongAttempts === 'number') setWrongAttempts(otherState.wrongAttempts);
    if (typeof otherState.firstTrySuccess === 'number') setFirstTrySuccess(otherState.firstTrySuccess);
  }, [otherState.resultsVisible]); // eslint-disable-line

  // --- Real-time: Try Again from other side ---
  useEffect(() => {
    const resetAt = otherState.resetAt;
    if (!resetAt || resetAt === prevResetAtRef.current) return;
    prevResetAtRef.current = resetAt;
    setSelectedLeft(null);
    setMatchedPairs([]);
    setErrorPair(null);
    setFlashPair(null);
    setResultsVisible(false);
    setWrongAttempts(0);
    setFirstTrySuccess(0);
    setAttemptsPerSentence({});
    syncedRef.current = false;
    if (Array.isArray(otherState.shuffledRight)) setShuffledRight(otherState.shuffledRight);
  }, [otherState.resetAt]); // eslint-disable-line

  const handleLeftClick = (index) => {
    if (isEditing) return;
    if (matchedPairs.some(p => p.leftIndex === index)) return;
    setSelectedLeft(index);
    updateState({ selectedLeft: index });
  };

  const handleRightClick = (index) => {
    if (isEditing) return;
    if (matchedPairs.some(p => p.rightValue === shuffledRight[index])) return;
    if (selectedLeft === null) return;

    const leftSentence = editableSentences[selectedLeft];
    const rightHalf = shuffledRight[index];

    if (leftSentence.right === rightHalf) {
      const capturedLeft = selectedLeft;
      const capturedRightValue = rightHalf;
      const isFirstTry = (attemptsPerSentence[capturedLeft] || 0) === 0;
      const capturedWrongAttempts = wrongAttempts;
      const capturedFirstTrySuccess = firstTrySuccess;

      const fp = { leftIndex: capturedLeft, rightValue: capturedRightValue };
      setFlashPair(fp);
      setSelectedLeft(null);
      updateState({ flashPair: fp, selectedLeft: null });

      setTimeout(() => {
        const newPair = { leftIndex: capturedLeft, rightValue: capturedRightValue };
        const newFirstTrySuccess = capturedFirstTrySuccess + (isFirstTry ? 1 : 0);
        setMatchedPairs(prev => {
          const newMatchedPairs = [...prev, newPair];
          const isAllMatched = newMatchedPairs.length === editableSentences.length;
          if (isAllMatched) {
            setResultsVisible(true);
            updateState({ matchedPairs: newMatchedPairs, resultsVisible: true, wrongAttempts: capturedWrongAttempts, firstTrySuccess: newFirstTrySuccess, flashPair: null });
          } else {
            updateState({ matchedPairs: newMatchedPairs, flashPair: null });
          }
          return newMatchedPairs;
        });
        setFlashPair(null);
        if (isFirstTry) setFirstTrySuccess(newFirstTrySuccess);
        setAttemptsPerSentence(prev => ({ ...prev, [capturedLeft]: (prev[capturedLeft] || 0) + 1 }));
      }, 800);
    } else {
      const ep = { leftIndex: selectedLeft, rightValue: rightHalf };
      setErrorPair(ep);
      setSelectedLeft(null);
      setWrongAttempts(prev => prev + 1);
      setAttemptsPerSentence(prev => ({ ...prev, [selectedLeft]: (prev[selectedLeft] || 0) + 1 }));
      updateState({ errorPair: ep, selectedLeft: null });
      setTimeout(() => {
        setErrorPair(null);
        updateState({ errorPair: null });
      }, 1000);
    }
  };

  const handleReset = () => {
    const newShuffle = shuffleArray(editableSentences.map(s => s.right));
    setSelectedLeft(null);
    setMatchedPairs([]);
    setErrorPair(null);
    setFlashPair(null);
    setResultsVisible(false);
    setWrongAttempts(0);
    setFirstTrySuccess(0);
    setAttemptsPerSentence({});
    setShuffledRight(newShuffle);
    syncedRef.current = false;
    updateState({
      matchedPairs: [], resultsVisible: false, wrongAttempts: 0, firstTrySuccess: 0,
      selectedLeft: null, flashPair: null, errorPair: null,
      shuffledRight: newShuffle, resetAt: Date.now()
    });
  };

  const handleSave = () => {
    setIsEditing(false);
    if (typeof updateAttributes === 'function') {
      updateAttributes({
        sentences: editableSentences,
        title: editableTitle,
        level,
      });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditableSentences([...sentences]);
    setEditableTitle(title);
  };

  const updateSentence = (index, field, value) => {
    setEditableSentences(prev =>
      prev.map((s, i) =>
        i === index ? { ...s, [field]: value } : s
      )
    );
  };

  const addSentence = () => {
    const newSentence = {
      id: `s${Date.now()}`,
      left: '',
      right: '',
      breakPoint: 0.5
    };
    setEditableSentences(prev => [...prev, newSentence]);
  };

  const deleteSentence = (index) => {
    if (editableSentences.length <= 1) return;
    setEditableSentences(prev => prev.filter((_, i) => i !== index));
  };

  // --- Class helpers: own state + mirror other side's state ---
  const getLeftClass = (index) => {
    if (matchedPairs.some(p => p.leftIndex === index)) return 'matched';
    if (flashPair?.leftIndex === index) return 'correct';
    if (otherState.flashPair?.leftIndex === index) return 'correct';
    if (selectedLeft === index) return 'selected';
    if (otherState.selectedLeft === index && selectedLeft === null) return 'selected';
    if (errorPair?.leftIndex === index) return 'error';
    if (otherState.errorPair?.leftIndex === index) return 'error';
    return '';
  };

  const getRightClass = (index) => {
    const val = shuffledRight[index];
    if (matchedPairs.some(p => p.rightValue === val)) return 'matched';
    if (flashPair?.rightValue === val) return 'correct';
    if (otherState.flashPair?.rightValue === val) return 'correct';
    if (errorPair?.rightValue === val) return 'error';
    if (otherState.errorPair?.rightValue === val) return 'error';
    return '';
  };

  // Results panel data — same ring/tier/headline pattern as the other
  // redesigned exercises, re-skinned with this exercise's rose accent.
  // Scored on first-try success rate, matching the original getRating().
  let resultsData = null;
  if (resultsVisible) {
    const total = editableSentences.length || 1;
    const percent = Math.round((firstTrySuccess / total) * 100);
    const circumference = 2 * Math.PI * 42;
    const dashOffset = circumference * (1 - percent / 100);
    let tier = 'practice';
    let headline;
    let subline;
    if (percent >= 90) { tier = 'excellent'; headline = 'Outstanding!'; subline = isStudent ? 'You nailed it.' : 'Student nailed it.'; }
    else if (percent >= 70) { tier = 'great'; headline = 'Great job!'; subline = 'Solid performance.'; }
    else if (percent >= 50) { tier = 'good'; headline = 'Good work.'; subline = isStudent ? 'Keep going — you’re close.' : 'Getting there.'; }
    else { tier = 'practice'; headline = 'Keep practicing'; subline = isStudent ? 'You’ll get it — try again.' : 'Review and try again.'; }
    resultsData = { percent, circumference, dashOffset, tier, headline, subline };
  }

  // Filter display columns using matchedPairs (value-based, sync-friendly)
  const getSortedLeft = () => {
    return editableSentences
      .map((s, i) => ({ ...s, _originalIndex: i }))
      .filter(s => !matchedPairs.some(p => p.leftIndex === s._originalIndex));
  };

  const getSortedRight = () => {
    return shuffledRight
      .map((s, i) => ({ value: s, _originalIndex: i }))
      .filter(s => !matchedPairs.some(p => p.rightValue === s.value));
  };

  return (
    <div className="mh-exercise-card" ref={containerRef}>
      {/* Header */}
      <div className="mh-header">
        <div className="mh-header-left">
          <div className="mh-header-icon"><MHIcon /></div>
          {isEditing ? (
            <input
              className="mh-title-input"
              value={editableTitle}
              onChange={e => setEditableTitle(e.target.value)}
            />
          ) : (
            <span className="mh-title">{editableTitle}</span>
          )}
          <span className="mh-level-badge">{level}</span>
        </div>
        <div className="mh-header-actions">
          {!isEditing && !isStudent && (
            <button className="mh-icon-btn" aria-label="Edit" data-tip="Edit" onClick={() => setIsEditing(true)}>
              <EditIcon />
            </button>
          )}
          {isEditing && (
            <>
              <button className="mh-icon-btn is-save" aria-label="Save" data-tip="Save" onClick={handleSave}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="9,12 11,14 15,10" />
                </svg>
              </button>
              <button className="mh-icon-btn is-cancel" aria-label="Cancel" data-tip="Cancel" onClick={handleCancel}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </>
          )}
          {onClose && (
            <button className="mh-icon-btn" onClick={onClose} aria-label="Close" data-tip="Close">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="eye-icon">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-open" />
                <circle cx="12" cy="12" r="3" className="eye-pupil" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="mh-body">
        {!isEditing && (
          <div className="mh-instructions">
            <p>Click on a <strong>sentence beginning</strong> (left side), then find its perfect <strong>ending</strong> (right side). When you match them correctly, they'll connect with a little animation!</p>
          </div>
        )}

        {isEditing ? (
          <div className="mh-editing-mode">
            <div className="mh-sentences-editor">
              {editableSentences.map((sentence, index) => (
                <div key={index} className="mh-sentence-editor-item">
                  <div className="mh-sentence-inputs">
                    <input
                      type="text"
                      value={sentence.left}
                      onChange={(e) => updateSentence(index, 'left', e.target.value)}
                      className="mh-left-input"
                      placeholder="Left half of sentence..."
                    />
                    <input
                      type="text"
                      value={sentence.right}
                      onChange={(e) => updateSentence(index, 'right', e.target.value)}
                      className="mh-right-input"
                      placeholder="Right half of sentence..."
                    />
                  </div>
                  {editableSentences.length > 1 && (
                    <button
                      className="mh-delete-btn"
                      onClick={() => deleteSentence(index)}
                      title="Delete sentence"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              ref={addBtnRef}
              className="mh-add-sentence-btn"
              onClick={addSentence}
            >
              + Add Sentence
            </button>
          </div>
        ) : (
          <div className="mh-matching-game">
            {!resultsVisible ? (
              <div className="mh-matching-container">
                <div className="mh-column">
                  <div className="mh-column-title">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M5 12h13M13 6l6 6-6 6"/>
                    </svg>
                    Sentence Beginnings
                  </div>
                  <p className="mh-column-subtitle">Click to select</p>
                  {getSortedLeft().map((sentence) => (
                    <div
                      key={sentence._originalIndex}
                      className={`mh-half ${getLeftClass(sentence._originalIndex)}`}
                      onClick={() => handleLeftClick(sentence._originalIndex)}
                    >
                      {sentence.left}
                    </div>
                  ))}
                </div>

                <div className="mh-column">
                  <div className="mh-column-title">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M5 3v18M5 4h11l-2 4 2 4H5"/>
                    </svg>
                    Sentence Endings
                  </div>
                  <p className="mh-column-subtitle">Find the match</p>
                  {getSortedRight().map((item) => (
                    <div
                      key={item._originalIndex}
                      className={`mh-half ${getRightClass(item._originalIndex)}`}
                      onClick={() => handleRightClick(item._originalIndex)}
                    >
                      {item.value}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className={`mh-results tier-${resultsData.tier}`}>
                <div className="mh-results-ring-wrap">
                  <svg viewBox="0 0 100 100" className="mh-results-ring" aria-hidden>
                    <circle cx="50" cy="50" r="42" className="mh-results-ring-track" />
                    <circle
                      cx="50"
                      cy="50"
                      r="42"
                      className="mh-results-ring-progress"
                      style={{ strokeDasharray: resultsData.circumference, strokeDashoffset: resultsData.dashOffset }}
                    />
                  </svg>
                  <div className="mh-results-ring-center">
                    <div className="mh-results-percent">{resultsData.percent}%</div>
                    <div className="mh-results-percent-label">first try</div>
                  </div>
                </div>
                <div className="mh-results-headline">{resultsData.headline}</div>
                <div className="mh-results-subline">{resultsData.subline}</div>
                <div className="mh-results-stats">
                  <div className="mh-results-stat is-correct">
                    <span className="mh-results-stat-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </span>
                    <span className="mh-results-stat-value">{firstTrySuccess}</span>
                    <span className="mh-results-stat-label">first-try</span>
                  </div>
                  <div className="mh-results-stats-divider" aria-hidden />
                  <div className="mh-results-stat is-incorrect">
                    <span className="mh-results-stat-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </span>
                    <span className="mh-results-stat-value">{wrongAttempts}</span>
                    <span className="mh-results-stat-label">wrong attempts</span>
                  </div>
                </div>
                <button className="mh-results-cta" onClick={handleReset}>
                  <span>Try Again</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <polyline points="23 4 23 10 17 10"/>
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                  </svg>
                </button>
              </div>
            )}

            {matchedPairs.length > 0 && !resultsVisible && (
              <div className="mh-matched-list">
                <h4>Matched Pairs ({matchedPairs.length}/{editableSentences.length})</h4>
                {matchedPairs.map((pair, idx) => {
                  const fullSentence = editableSentences[pair.leftIndex];
                  return (
                    <div key={idx} className="mh-matched-row">
                      <span className="mh-matched-sentence">
                        {fullSentence.left} {fullSentence.right}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchingHalvesExercise;
